import { PermissionTypes } from "@certa/types";
import {
  useIsECAUIEnabled,
  useIsOS3FlowViewEnabled
} from "../hooks/useCanViewStudio2";
import { useCheckPermission } from "../utils/permissions/Chowkidar";
import { useDesignAIEnabled } from "./useDesignAIEnabled";
import { createRef, useImperativeHandle } from "react";
import { ToastPlacements } from "@certa/catalyst/components/Toast";
import { useInstanceConfigQuery } from "@certa/queries/hooks/config.hook";

const DISABLE_DESIGN_AI_FORCE_FEEDBACK = "DISABLE_DESIGN_AI_FORCE_FEEDBACK";
const CONFIG_DRAWER_ON_RIGHT = "CONFIG_DRAWER_ON_RIGHT";
const UNDO_REDO_AT_BOTTOM = "UNDO_REDO_AT_BOTTOM";
const WORKFLOW_VIEW_TABS_IN_CANVAS = "WORKFLOW_VIEW_TABS_IN_CANVAS";

const DISABLE_DESIGNAI_DRAWER = "DISABLE_DESIGNAI_DRAWER";
const DISABLE_AI_APP_BUILDER = "DISABLE_AI_APP_BUILDER";
const SHOW_CONVERSATION_ID = "SHOW_CONVERSATION_ID";
const DISABLE_STUDIO_REPORT_ISSUE = "DISABLE_STUDIO_REPORT_ISSUE";
const DISABLE_STUDIO_V3 = "DISABLE_STUDIO_V3";
const SHOW_SAVEPOINT_DROPDOWN_IN_ACTION_MENU =
  "SHOW_SAVEPOINT_DROPDOWN_IN_ACTION_MENU";

export const useDesignAIDrawerEnabled = (): boolean => {
  const isDesignAIEnabled = useDesignAIEnabled();

  const isECAUIEnabled = useIsECAUIEnabled();

  const { data: config } = useInstanceConfigQuery();

  const isDesignAIDrawerEnabled =
    !config?.custom_ui_labels.specialFeatures?.includes(
      DISABLE_DESIGNAI_DRAWER
    );

  return isDesignAIEnabled && isDesignAIDrawerEnabled && isECAUIEnabled;
};

export const useDesignAIForceFeedbackEnabled = (): boolean => {
  const { data: config } = useInstanceConfigQuery();
  const isEnabled = !config?.custom_ui_labels.specialFeatures?.includes(
    DISABLE_DESIGN_AI_FORCE_FEEDBACK
  );

  return isEnabled;
};

export const designAIToastPlacementRef =
  createRef<ToastPlacements | null>() as React.MutableRefObject<ToastPlacements | null>;
designAIToastPlacementRef.current = ToastPlacements.BOTTOM_RIGHT;

export const useConfigDrawerRightPosition = (): boolean => {
  const isDesignAIDrawerEnabled = useDesignAIDrawerEnabled();
  const { data: config } = useInstanceConfigQuery();
  const isRight = !!config?.custom_ui_labels.specialFeatures?.includes(
    CONFIG_DRAWER_ON_RIGHT
  );
  const isDrawerOnRight = isRight || !isDesignAIDrawerEnabled;
  useImperativeHandle(designAIToastPlacementRef, () => {
    return isDrawerOnRight
      ? ToastPlacements.BOTTOM_LEFT
      : ToastPlacements.BOTTOM_RIGHT;
  });

  return isDrawerOnRight;
};

export const useUndoRedoAtBottom = (): boolean => {
  const isDesignAIDrawerEnabled = useDesignAIDrawerEnabled();
  const { data: config } = useInstanceConfigQuery();
  const isBottom =
    !!config?.custom_ui_labels.specialFeatures?.includes(UNDO_REDO_AT_BOTTOM);

  return isBottom || !isDesignAIDrawerEnabled;
};

export const useWorkflowViewTabsInCanvas = (): boolean => {
  const isDesignAIDrawerEnabled = useDesignAIDrawerEnabled();
  const { data: config } = useInstanceConfigQuery();
  const isCanvas = !!config?.custom_ui_labels.specialFeatures?.includes(
    WORKFLOW_VIEW_TABS_IN_CANVAS
  );

  return isCanvas || !isDesignAIDrawerEnabled;
};

export const useIsAPPBuilderAgentEnabled = (): boolean => {
  const canViewStudio2Permissions = useCheckPermission(
    PermissionTypes.CAN_ACCESS_STUDIO
  );
  const { data: config } = useInstanceConfigQuery();
  const isStudioEnabled = !!config?.configuration.enable_studio;

  const isDesignAIEnabled = useDesignAIEnabled();

  const isEnabled = !config?.custom_ui_labels.specialFeatures?.includes(
    DISABLE_AI_APP_BUILDER
  );

  return (
    isStudioEnabled &&
    canViewStudio2Permissions &&
    isEnabled &&
    isDesignAIEnabled
  );
};

export const useIsShowConversationIdEnabled = (): boolean => {
  const { data: config } = useInstanceConfigQuery();
  return !!config?.custom_ui_labels.specialFeatures?.includes(
    SHOW_CONVERSATION_ID
  );
};

export const useIsReportIssueEnabled = (): boolean => {
  const { data: config } = useInstanceConfigQuery();
  const isEnabled = !config?.custom_ui_labels.specialFeatures?.includes(
    DISABLE_STUDIO_REPORT_ISSUE
  );
  const isNotProductionOrStaging = ![
    "certain",
    "certaeu",
    "certasite"
  ].includes(import.meta.env.REACT_APP_CERTA_ENV || "");

  return isNotProductionOrStaging && isEnabled;
};

export const useIsStudioV3Enabled = (): boolean => {
  const isDesignAIDrawerEnabled = useDesignAIDrawerEnabled();
  const isOS3FlowViewEnabled = useIsOS3FlowViewEnabled();
  const { data: config } = useInstanceConfigQuery();
  const isStudioV3Enabled =
    !config?.custom_ui_labels.specialFeatures?.includes(DISABLE_STUDIO_V3);
  return isStudioV3Enabled && isOS3FlowViewEnabled && isDesignAIDrawerEnabled;
};

export const useShouldShowSavepointDropdownInActionMenu = (): boolean => {
  const { data: config } = useInstanceConfigQuery();
  return !!config?.custom_ui_labels.specialFeatures?.includes(
    SHOW_SAVEPOINT_DROPDOWN_IN_ACTION_MENU
  );
};
